/**
 * Footprint shapes, in two halves.
 *
 * The first half generates authoritative cells. In production the engine decides
 * which cells an impact touches and the renderer never recomputes it — these
 * generators exist for the lab, for fixtures, and for client-side telegraphs of a
 * skill whose targeting the server has not resolved yet.
 *
 * The second half is what the renderer actually needs from a footprint hint: the
 * ideal mathematical shape behind the blocky cell set, so `ideal-shape` and
 * `clipped-shape` have something clean to intersect the cells with. A cone widens
 * continuously; a line is one ribbon; a ring keeps a hole. None of that survives if
 * the only thing you have is squares.
 *
 * All of it is grid space: units are cells, x east, z south, cell centres at
 * (col + 0.5, row + 0.5).
 */

import { seededRandom, cellKey } from './topology';

export function directionVector(degrees) {
  const radians = (degrees * Math.PI) / 180;
  return { x: Math.cos(radians), z: Math.sin(radians) };
}

/**
 * A supercover walk rather than Bresenham: on a diagonal, Bresenham steps corner to
 * corner and the ribbon falls apart into two components with a gap down the middle.
 * Bridging the corner crossing keeps the line four-connected, which is what makes it
 * one effect.
 */
function lineCells(origin, size, direction, bounds, push) {
  const originX = origin.col + 0.5;
  const originZ = origin.row + 0.5;
  const steps = Math.max(1, size) * 24;
  let x = originX;
  let z = originZ;
  let prevCol = origin.col;
  let prevRow = origin.row;
  push(prevCol, prevRow);

  for (let i = 0; i < steps; i++) {
    x += (direction.x * size) / steps;
    z += (direction.z * size) / steps;
    const col = Math.floor(x);
    const row = Math.floor(z);
    if (col !== prevCol && row !== prevRow) {
      const a = Math.abs((col + 0.5 - originX) * direction.z - (prevRow + 0.5 - originZ) * direction.x);
      const b = Math.abs((prevCol + 0.5 - originX) * direction.z - (row + 0.5 - originZ) * direction.x);
      if (a <= b) push(col, prevRow); else push(prevCol, row);
    }
    push(col, row);
    prevCol = col;
    prevRow = row;
  }
}

/**
 * @param hint      one of single | line | burst | ring | cone | cluster
 * @param options   origin cell, size in cells, direction in degrees, grid bounds,
 *                  seed, and an optional passable(col,row) predicate so a generated
 *                  shape does not spill through a wall
 */
export function generateFootprintCells(hint, options = {}) {
  const {
    origin = { col: 0, row: 0 },
    size = 3,
    directionDegrees = 0,
    columns = 24,
    rows = 24,
    seed = 1,
    passable = null,
    detached = false,
  } = options;

  const direction = directionVector(directionDegrees);
  const seen = new Set();
  const cells = [];
  const inBounds = (col, row) => col >= 0 && col < columns && row >= 0 && row < rows;
  const push = (col, row) => {
    if (!inBounds(col, row)) return;
    if (passable && !passable(col, row)) return;
    const key = cellKey(col, row);
    if (seen.has(key)) return;
    seen.add(key);
    cells.push({ col, row });
  };

  const originX = origin.col + 0.5;
  const originZ = origin.row + 0.5;

  if (hint === 'single') {
    push(origin.col, origin.row);
  } else if (hint === 'line') {
    lineCells(origin, size, direction, { columns, rows }, push);
  } else if (hint === 'burst') {
    const radius = size + 0.15;
    for (let col = 0; col < columns; col++) {
      for (let row = 0; row < rows; row++) {
        const dx = col + 0.5 - originX;
        const dz = row + 0.5 - originZ;
        if (dx * dx + dz * dz <= radius * radius) push(col, row);
      }
    }
  } else if (hint === 'ring') {
    const radius = Math.max(2, size);
    for (let col = 0; col < columns; col++) {
      for (let row = 0; row < rows; row++) {
        const distance = Math.hypot(col + 0.5 - originX, row + 0.5 - originZ);
        if (distance >= radius - 1.05 && distance <= radius + 0.05) push(col, row);
      }
    }
  } else if (hint === 'cone') {
    const radius = size + 0.6;
    const half = Math.PI / 6 + 0.06;
    push(origin.col, origin.row);
    for (let col = 0; col < columns; col++) {
      for (let row = 0; row < rows; row++) {
        const dx = col + 0.5 - originX;
        const dz = row + 0.5 - originZ;
        const distance = Math.hypot(dx, dz);
        if (distance < 0.1 || distance > radius) continue;
        const cosine = (dx * direction.x + dz * direction.z) / distance;
        if (Math.acos(Math.min(1, Math.max(-1, cosine))) <= half) push(col, row);
      }
    }
  } else if (hint === 'cluster') {
    const random = seededRandom(seed * 7919 + 11);
    const target = 5 + size * 3;
    push(origin.col, origin.row);
    let guard = 0;
    while (cells.length < target && guard++ < 600) {
      const from = cells[Math.floor(random() * cells.length)];
      const [dc, dr] = [[1, 0], [-1, 0], [0, 1], [0, -1]][Math.floor(random() * 4)];
      push(from.col + dc, from.row + dr);
    }
    if (detached) {
      const clear = (col, row, exclude = []) => cells.every(cell => (
        exclude.includes(cell) || Math.abs(cell.col - col) > 1 || Math.abs(cell.row - row) > 1
      ));
      let base = null;
      let tries = 0;
      while (!base && tries++ < 200) {
        const col = Math.floor(random() * columns);
        const row = Math.floor(random() * rows);
        if (inBounds(col, row) && clear(col, row) && (!passable || passable(col, row))) base = { col, row };
      }
      if (base) {
        const before = cells.length;
        push(base.col, base.row);
        const blob = cells.slice(before);
        let blobGuard = 0;
        while (blob.length < 3 + Math.floor(size / 2) && blobGuard++ < 300) {
          const from = blob[Math.floor(random() * blob.length)];
          const [dc, dr] = [[1, 0], [-1, 0], [0, 1], [0, -1]][Math.floor(random() * 4)];
          const col = from.col + dc;
          const row = from.row + dr;
          if (!inBounds(col, row) || seen.has(cellKey(col, row))) continue;
          if (!clear(col, row, blob)) continue;
          const at = cells.length;
          push(col, row);
          if (cells.length > at) blob.push(cells[cells.length - 1]);
        }
      }
    }
  }

  return cells;
}

/**
 * The ideal shape behind the cells, in grid space, for the merge modes and for the
 * flow the material animates along.
 *
 * `reach` is how far from the origin the effect extends; intensity, particle travel
 * and growth fronts are all normalized by it so a two-cell burst and a nine-cell
 * burst move at the same visual speed rather than the big one crawling.
 */
export function footprintParams(hint, options = {}) {
  const {
    origin = { col: 0, row: 0 },
    size = 3,
    directionDegrees = 0,
    cells = [],
  } = options;

  const direction = directionVector(directionDegrees);
  const params = {
    origin: { x: origin.col + 0.5, z: origin.row + 0.5 },
    direction: { x: direction.x, z: direction.z },
    radius: size + 0.5,
    halfWidth: 0.55,
    length: size,
    halfAngle: Math.PI / 6,
    reach: size + 0.55,
  };

  if (hint === 'single') {
    params.radius = 0.55;
    params.reach = 0.8;
  } else if (hint === 'line') {
    params.halfWidth = 0.5;
    params.reach = size;
  } else if (hint === 'ring') {
    params.radius = Math.max(2, size) - 0.5;
    params.halfWidth = 0.58;
    params.reach = params.radius + 0.6;
  } else if (hint === 'cone') {
    params.radius = size + 0.6;
    params.reach = size + 0.6;
  } else if (hint === 'cluster') {
    let reach = 0.8;
    cells.forEach(cell => {
      const distance = Math.hypot(cell.col + 0.5 - params.origin.x, cell.row + 0.5 - params.origin.z) + 0.7;
      if (distance > reach) reach = distance;
    });
    params.reach = reach;
  }

  return params;
}

/** Positive inside the ideal shape, in cells. The CPU twin of `idealSD` in the shader. */
export function idealSignedDistance(hint, params, x, z) {
  const dx = x - params.origin.x;
  const dz = z - params.origin.z;
  if (hint === 'single' || hint === 'burst') return params.radius - Math.hypot(dx, dz);
  if (hint === 'ring') return params.halfWidth - Math.abs(Math.hypot(dx, dz) - params.radius);
  if (hint === 'line') {
    const t = Math.max(0, Math.min(params.length, dx * params.direction.x + dz * params.direction.z));
    const px = params.origin.x + params.direction.x * t;
    const pz = params.origin.z + params.direction.z * t;
    return params.halfWidth - Math.hypot(x - px, z - pz);
  }
  if (hint === 'cone') {
    const distance = Math.hypot(dx, dz);
    if (distance < 0.15) return params.radius;
    const cosine = (dx * params.direction.x + dz * params.direction.z) / distance;
    const angle = Math.acos(Math.min(1, Math.max(-1, cosine)));
    return Math.min(params.radius - distance, (params.halfAngle - angle) * Math.max(distance, 0.001));
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * A sampler over the merged emission domain.
 *
 * Particles come from anywhere in the merged area — never one emitter per cell,
 * which is the tell that gives away thirteen effects pretending to be one.
 */
export function domainSampler(component, hint, params, mergeMode, random) {
  const insideCells = (x, z) => component.keys.has(cellKey(Math.floor(x), Math.floor(z)));
  const insideIdeal = (x, z) => (
    hint === 'cluster' ? insideCells(x, z) : idealSignedDistance(hint, params, x, z) >= 0
  );
  const accept = (x, z) => {
    if (mergeMode === 'strict-cells') return insideCells(x, z);
    if (mergeMode === 'ideal-shape') return insideIdeal(x, z);
    return insideCells(x, z) && insideIdeal(x, z);
  };

  const { bounds } = component;
  let minX = bounds.minCol;
  let maxX = bounds.maxCol;
  let minZ = bounds.minRow;
  let maxZ = bounds.maxRow;
  if (mergeMode === 'ideal-shape') {
    minX = Math.min(minX, params.origin.x - params.reach);
    maxX = Math.max(maxX, params.origin.x + params.reach);
    minZ = Math.min(minZ, params.origin.z - params.reach);
    maxZ = Math.max(maxZ, params.origin.z + params.reach);
  }

  return function sample() {
    for (let attempt = 0; attempt < 48; attempt++) {
      const x = minX + random() * (maxX - minX);
      const z = minZ + random() * (maxZ - minZ);
      if (accept(x, z)) return { x, z };
    }
    return { x: params.origin.x, z: params.origin.z };
  };
}
